import { useEffect, useId, useMemo, useState } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Check, ChevronsUpDown } from 'lucide-react'
import { cn } from '@/utils'
import { useKeyboardFirstCommandList } from './hooks/use-keyboard-first-command-list'

interface SelectCategoryComboBoxProps {
  options: string[]
  placeholder?: string
  /** Optional label rendered above the trigger (e.g. "Category" — WMPCM-10942 AC-1). Omitted by
   *  callers (e.g. `BackgroundsPanel`) that don't want one, so their layout is unaffected. */
  label?: string
  /** Controlled selected value. Falls back to internal state (defaulting to `placeholder`) when
   *  omitted, so existing uncontrolled callers keep working unchanged. */
  value?: string
  onValueChange?: (value: string) => void
  /** Reports open/close so a host panel can track "is one of my dropdowns open" (e.g. for an
   *  Escape-key priority chain) without owning this component's internal state. */
  onOpenChange?: (open: boolean) => void
  /**
   * Keyboard-first opening (Backgrounds Type): the search field takes focus on open, Tab moves
   * focus onto the option list (starting on the selected option) and Shift+Tab back, the arrow
   * keys move through the options, and Enter or Space picks one. Enter on an untouched list just
   * closes it (WMPCM-11322). Off: focus stays on the trigger. See `useKeyboardFirstCommandList`.
   */
  focusSearchOnOpen?: boolean
}

export function SelectCategoryComboBox({
  options,
  placeholder,
  label,
  value,
  onValueChange,
  onOpenChange,
  focusSearchOnOpen = false,
}: SelectCategoryComboBoxProps) {
  const labelId = useId()
  const triggerId = useId()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [localSelected, setLocalSelected] = useState(placeholder) // Default is Select category
  const selected = value ?? localSelected

  const filtered = useMemo(
    () => options.filter((category) => category.toLowerCase().includes(search.toLowerCase())),
    [options, search]
  )

  function handleSelect(val: string) {
    setLocalSelected(val)
    onValueChange?.(val)
    handleOpenChange(false)
  }

  function handleOpenChange(next: boolean) {
    setOpen(next)
    onOpenChange?.(next)
    // Every close (pick, Escape, outside click, Tab out) clears the filter, so reopening shows the
    // full list — matching MultiSelectChipCombobox.
    if (!next) setSearch('')
    if (next) keyboard.resetHighlight()
  }

  const keyboard = useKeyboardFirstCommandList({
    enabled: focusSearchOnOpen,
    visibleLabels: filtered,
    close: () => handleOpenChange(false),
    pick: handleSelect,
    preferredLabel: selected,
  })
  const { listRef } = keyboard

  // AC-2 (WMPCM-10942): opening the dropdown scrolls the currently selected category into view.
  // Not left to cmdk's own highlight-scroll behavior, which tracks keyboard/mouse hover state,
  // not "the chosen value" — this looks up the option by its own data attribute instead.
  useEffect(() => {
    if (!open) return

    const raf = requestAnimationFrame(() => {
      const node = listRef.current?.querySelector<HTMLElement>(
        `[data-category-option="${CSS.escape(selected ?? '')}"]`
      )

      node?.scrollIntoView({ block: 'nearest' })
    })

    return () => cancelAnimationFrame(raf)
  }, [listRef, open, selected])

  return (
    <div className="flex flex-col gap-2 w-[var(--panel-dropdown-width)]">
      {label ? (
        <span id={labelId} className="text-sm font-medium leading-none text-muted-foreground">
          {label}
        </span>
      ) : null}
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            ref={keyboard.triggerRef}
            id={triggerId}
            type="button"
            // The visible label names the control: "Category, All" rather than just "All". The
            // trigger lists itself so its own text (the selected value) is still part of the name.
            aria-labelledby={label ? `${labelId} ${triggerId}` : undefined}
            className="w-[var(--panel-dropdown-width)] h-9 px-3 flex justify-between items-center rounded-md border font-medium text-sm bg-card"
          >
            {selected}
            <ChevronsUpDown className="ml-2 opacity-50 shrink-0 size-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--panel-dropdown-width)] p-0 mt-2 rounded-md shadow-lg"
          align="start"
          // Keeps focus on the trigger button when the popover opens (click or Enter) instead of
          // Radix's default of auto-focusing the first focusable descendant (the search input
          // here). Without this, a second Enter press on the trigger — meant to close the popover
          // the same way a second click does — lands on cmdk's own Enter handling instead and
          // silently picks the first/highlighted category rather than closing (WMPCM-11322).
          //
          // With `focusSearchOnOpen` the search field takes focus instead; Enter there closes the
          // list until an option has been highlighted (see `useKeyboardFirstCommandList`).
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            keyboard.focusSearch()
          }}
        >
          <Command {...keyboard.commandProps}>
            <CommandInput
              ref={keyboard.inputRef}
              placeholder="Search"
              value={search}
              onValueChange={(next) => {
                keyboard.markSearchTyped()
                setSearch(next)
              }}
              onKeyDown={keyboard.searchKeyDown}
              className="h-9"
            />
            <CommandList ref={keyboard.setListElement} {...keyboard.listProps}>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {filtered.map((cat) => {
                  const isSelected = cat === selected

                  return (
                    <CommandItem
                      key={cat}
                      value={cat}
                      data-category-option={cat}
                      onSelect={() => handleSelect(cat)}
                      className="flex items-center justify-between px-3 py-2 cursor-pointer"
                    >
                      <span>{cat}</span>
                      <Check
                        className={cn('size-4', isSelected ? 'opacity-100' : 'opacity-0')}
                        aria-hidden="true"
                      />
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
